import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  api,
  type ProductionPlanTodayItem,
  type ProductionPlanOverdueItem,
  type ProductionPlanToday,
} from '../api/client'

// Phase 4-C: work_type表示名
const WORK_TYPE_LABELS: Record<string, string> = {
  story:        'ストーリー',
  book:         '単行本作業',
  tankobon:     '単行本作業',
  color:        'カラーイラスト',
  kaikai_kiki:  'カイカイキキ',
  other:        'その他',
}

// ────────────────────────────────────────────────────────────
// 定数
// ────────────────────────────────────────────────────────────

const RISK_LABEL: Record<string, string> = {
  critical: '破綻見込み',
  danger:   '危険',
  warning:  '注意',
  safe:     '安全',
}

const RISK_STYLE: Record<string, { bg: string; border: string; text: string }> = {
  critical: { bg: 'var(--red-light)',   border: 'var(--red)',   text: 'var(--red)'   },
  danger:   { bg: 'var(--red-light)',   border: 'var(--red)',   text: 'var(--red)'   },
  warning:  { bg: 'var(--amber-light)', border: 'var(--amber)', text: 'var(--amber)' },
  safe:     { bg: 'var(--green-light)', border: 'var(--green)', text: 'var(--green)' },
}

const STATUS_BADGE: Record<string, { label: string; bg: string; color: string }> = {
  planned: { label: '予定',     bg: 'var(--blue-light)',  color: 'var(--blue)'  },
  done:    { label: '完了',     bg: 'var(--green-light)', color: 'var(--green)' },
  skipped: { label: 'スキップ', bg: 'var(--paper-3)',     color: 'var(--ink-3)' },
}

// ────────────────────────────────────────────────────────────
// ユーティリティ
// ────────────────────────────────────────────────────────────

function amtText(planned_amount: number, unit: string): string {
  if (unit === 'pages') return `${planned_amount}P`
  if (unit === 'days')  return `${planned_amount}日`
  return String(planned_amount)
}

function epText(episode_number: number | null): string {
  return episode_number != null ? ` #${episode_number}` : ''
}

function mmdd(plan_date: string): string {
  return plan_date.slice(5).replace('-', '/')
}

// ────────────────────────────────────────────────────────────
// サブコンポーネント
// ────────────────────────────────────────────────────────────

function OverdueSection({ items }: { items: ProductionPlanOverdueItem[] }) {
  return (
    <div style={{
      marginBottom: 12, padding: '10px 14px',
      background: 'var(--amber-light)',
      border: '1px solid rgba(176,122,18,0.3)',
      borderRadius: 6,
    }}>
      <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--amber)', marginBottom: 6 }}>
        ⚠ 未完了の過去予定があります
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {items.map((item) => (
          <div key={item.id} style={{
            fontSize: 13, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
          }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-3)', minWidth: 32 }}>
              {mmdd(item.plan_date)}
            </span>
            <span>
              {item.work_title}{epText(item.episode_number)}
            </span>
            {item.stage_label && (
              <span style={{ color: 'var(--ink-3)' }}>/ {item.stage_label}</span>
            )}
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
              {amtText(item.planned_amount, item.unit)}
            </span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8 }}>
        <Link to="/works/production-plan" style={{ textDecoration: 'none' }}>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--amber)' }}>
            制作計画で確認
          </button>
        </Link>
      </div>
    </div>
  )
}

function TodayItemRow({
  item,
  updatingId,
  onUpdate,
  onDefer,
}: {
  item: ProductionPlanTodayItem
  updatingId: string | null
  onUpdate: (id: string, status: 'done' | 'skipped' | 'planned') => void
  onDefer: (id: string) => void
}) {
  const rs  = RISK_STYLE[item.risk ?? 'safe'] ?? RISK_STYLE.safe
  const sb  = STATUS_BADGE[item.status] ?? STATUS_BADGE.planned
  const isDone     = item.status === 'done'
  const isUpdating = updatingId === item.id

  // stage_labelの内部値を表示名に変換
  const displayLabel = item.stage_label
    ? item.stage_label
    : (WORK_TYPE_LABELS[item.stage_key ?? ''] ?? null)

  return (
    <div style={{
      padding: '10px 14px',
      background: isDone ? 'var(--paper-2)' : rs.bg,
      borderLeft: `3px solid ${isDone ? 'var(--paper-3)' : rs.border}`,
      borderRadius: 6,
      opacity: isDone ? 0.7 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
        {/* バッジ群 */}
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', paddingTop: 1 }}>
          {!isDone && item.risk && (
            <span style={{
              fontSize: 10, padding: '2px 6px', borderRadius: 3, whiteSpace: 'nowrap',
              background: rs.bg, color: rs.text, border: `1px solid ${rs.border}`,
            }}>
              {RISK_LABEL[item.risk] ?? item.risk}
            </span>
          )}
          <span style={{
            fontSize: 10, padding: '2px 6px', borderRadius: 3, whiteSpace: 'nowrap',
            background: sb.bg, color: sb.color, border: `1px solid ${sb.color}33`,
          }}>
            {sb.label}
          </span>
          {/* 延期元表示 */}
          {item.moved_from_date && (
            <span style={{
              fontSize: 10, padding: '2px 6px', borderRadius: 3, whiteSpace: 'nowrap',
              background: 'var(--amber-light)', color: 'var(--amber)',
              border: '1px solid rgba(176,122,18,0.3)',
            }}>
              繰越({mmdd(item.moved_from_date)})
            </span>
          )}
        </div>

        {/* 内容 */}
        <div style={{ flex: 1, minWidth: 140 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {item.work_title}{epText(item.episode_number)}
            {displayLabel && (
              <span style={{ fontWeight: 400, color: 'var(--ink-3)', marginLeft: 4 }}>
                / {displayLabel}
              </span>
            )}
          </div>
          <div style={{
            fontSize: 14, fontFamily: 'var(--font-mono)', fontWeight: 700, marginTop: 2,
          }}>
            {amtText(item.planned_amount, item.unit)}
          </div>
          {item.reason && !isDone && (
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>
              {item.reason}
            </div>
          )}
        </div>

        {/* アクションボタン */}
        <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {!isDone && (
            <button
              className="btn btn-sm btn-outline"
              style={{ fontSize: 11, color: 'var(--green)', borderColor: 'rgba(45,122,58,0.5)' }}
              disabled={isUpdating}
              onClick={() => onUpdate(item.id, 'done')}
            >
              {isUpdating ? '...' : '完了'}
            </button>
          )}
          {isDone && (
            <button
              className="btn btn-sm btn-ghost"
              style={{ fontSize: 11, color: 'var(--ink-3)' }}
              disabled={isUpdating}
              onClick={() => onUpdate(item.id, 'planned')}
            >
              {isUpdating ? '...' : '戻す'}
            </button>
          )}
          {!isDone && (
            <button
              className="btn btn-sm btn-ghost"
              style={{ fontSize: 11, color: 'var(--amber)' }}
              disabled={isUpdating}
              onClick={() => onDefer(item.id)}
              title="翌稼働日に繰り越す"
            >
              {isUpdating ? '...' : '延期'}
            </button>
          )}
          {!isDone && (
            <button
              className="btn btn-sm btn-ghost"
              style={{ fontSize: 11, color: 'var(--ink-3)' }}
              disabled={isUpdating}
              onClick={() => onUpdate(item.id, 'skipped')}
            >
              {isUpdating ? '...' : 'スキップ'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// メインコンポーネント
// ────────────────────────────────────────────────────────────

interface Props {
  compact?: boolean
  showUpcoming?: boolean
  /** 値が変わるたびに再取得する（他画面での工程更新後にこのパネルを最新化するため） */
  reloadKey?: number | string
}

export default function TodayProductionPlanPanel({ compact, showUpcoming, reloadKey }: Props) {
  const [data,       setData]       = useState<ProductionPlanToday | null>(null)
  const [loading,    setLoading]    = useState(true)
  const [error,      setError]      = useState<string | null>(null)
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [showAllModal, setShowAllModal] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    api.productionPlanning
      .todayPlan()
      .then(setData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load, reloadKey])

  const handleUpdate = async (id: string, status: 'done' | 'skipped' | 'planned') => {
    setUpdatingId(id)
    try {
      await api.productionPlanning.updatePlanItem(id, { status })
      load()
    } finally {
      setUpdatingId(null)
    }
  }

  const handleDefer = async (id: string) => {
    setUpdatingId(id)
    try {
      await api.productionPlanning.deferPlanItem(id)
      load()
    } finally {
      setUpdatingId(null)
    }
  }

  // ─── compact mode（Works.tsx 用） ──────────────────────────
  if (compact) {
    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">保存済み制作予定</span>
          <Link to="/works/production-plan" style={{ textDecoration: 'none' }}>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>制作計画を見る</button>
          </Link>
        </div>
        <div className="card-body">
          {loading && (
            <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>読み込み中...</div>
          )}
          {!loading && error && (
            <div style={{ color: 'var(--red)', fontSize: 13 }}>取得エラー: {error}</div>
          )}
          {!loading && !error && data && data.today_items.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
              <div style={{ marginBottom: 6 }}>今日の保存済み制作予定はありません。</div>
              <Link to="/works/production-plan" style={{ color: 'var(--blue)', fontSize: 12 }}>
                制作計画を生成する →
              </Link>
            </div>
          )}
          {!loading && !error && data && data.today_items.length > 0 && (
            <>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 8 }}>
                今日は保存済み計画では以下を進める予定です。
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {data.today_items.map((item) => {
                  const sb = STATUS_BADGE[item.status] ?? STATUS_BADGE.planned
                  return (
                    <div key={item.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                      opacity: item.status === 'done' ? 0.6 : 1,
                      padding: '4px 0',
                      borderBottom: '1px solid var(--paper-2)',
                    }}>
                      <span style={{
                        fontSize: 10, padding: '1px 5px', borderRadius: 3,
                        background: sb.bg, color: sb.color,
                        border: `1px solid ${sb.color}33`,
                        whiteSpace: 'nowrap', flexShrink: 0,
                      }}>
                        {sb.label}
                      </span>
                      <span style={{ fontWeight: 500, flex: 1, minWidth: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.work_title}{epText(item.episode_number)}
                        {item.stage_label && (
                          <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>
                            {' '}/ {item.stage_label}
                          </span>
                        )}
                      </span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, flexShrink: 0 }}>
                        {amtText(item.planned_amount, item.unit)}
                      </span>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  // ─── full mode（Dashboard.tsx 用） ─────────────────────────
  const TODAY_LIMIT    = 8
  const UPCOMING_LIMIT = 5
  const OVERDUE_LIMIT  = 5

  const todayItems    = data?.today_items    ?? []
  const overdueItems  = data?.overdue_items  ?? []
  const upcomingItems = data?.upcoming_items ?? []
  const overdueCount  = overdueItems.length
  const upcomingCount = upcomingItems.length

  // 今日: 未完了を先、完了を後ろに並べて最大 TODAY_LIMIT 件表示
  const undoneToday  = todayItems.filter((i) => i.status !== 'done')
  const doneToday    = todayItems.filter((i) => i.status === 'done')
  const sortedToday  = [...undoneToday, ...doneToday]
  const shownToday   = sortedToday.slice(0, TODAY_LIMIT)
  const hiddenToday  = sortedToday.length - shownToday.length

  const shownOverdue  = overdueItems.slice(0, OVERDUE_LIMIT)
  const hiddenOverdue = overdueItems.length - shownOverdue.length

  const shownUpcoming  = upcomingItems.slice(0, UPCOMING_LIMIT)
  const hiddenUpcoming = upcomingItems.length - shownUpcoming.length

  // ダッシュボードが縦に長くなりすぎないよう、隠れている件数の合計が
  // あるときだけ「全件表示」ボタン（モーダル）を出す
  const totalHidden = hiddenToday + hiddenOverdue + hiddenUpcoming

  const isAllEmpty   = !loading && !error && data != null
    && todayItems.length === 0 && overdueCount === 0 && upcomingCount === 0

  return (
    <>
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <span className="card-title">今日の制作予定</span>
        <Link to="/works/production-plan" style={{ textDecoration: 'none' }}>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>制作計画を見る →</button>
        </Link>
      </div>

      <div className="card-body">
        {loading && (
          <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>読み込み中...</div>
        )}
        {!loading && error && (
          <div style={{ color: 'var(--red)', fontSize: 13 }}>取得エラー: {error}</div>
        )}

        {/* 完全に予定なし → 空状態 */}
        {isAllEmpty && (
          <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
            <div style={{ marginBottom: 6 }}>保存済みの制作計画がありません。</div>
            <div style={{ fontSize: 12, marginBottom: 12 }}>
              制作計画を生成すると、今日やる作業をここに表示できます。
            </div>
            <Link to="/works/production-plan" style={{ textDecoration: 'none' }}>
              <button className="btn btn-outline btn-sm" style={{ fontSize: 12 }}>
                制作計画を作る
              </button>
            </Link>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* 過去未完了の予定（最大 OVERDUE_LIMIT 件） */}
            {overdueCount > 0 && (
              <OverdueSection items={shownOverdue} />
            )}

            {/* 今日の予定リスト（最大 TODAY_LIMIT 件、完了は後） */}
            {shownToday.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {shownToday.map((item) => (
                  <TodayItemRow
                    key={item.id}
                    item={item}
                    updatingId={updatingId}
                    onUpdate={handleUpdate}
                    onDefer={handleDefer}
                  />
                ))}
              </div>
            )}

            {/* 今日は0件・過去/近日はある場合の案内 */}
            {todayItems.length === 0 && (overdueCount > 0 || upcomingCount > 0) && (
              <div style={{ fontSize: 13, color: 'var(--ink-3)', paddingBottom: 4 }}>
                今日の保存済み予定はありません。
              </div>
            )}

            {/* 近日の予定（最大 UPCOMING_LIMIT 件） */}
            {showUpcoming && upcomingCount > 0 && (
              <div style={{ marginTop: shownToday.length > 0 ? 12 : 0 }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, color: 'var(--ink-3)',
                  marginBottom: 4, paddingTop: shownToday.length > 0 ? 8 : 0,
                  borderTop: shownToday.length > 0 ? '1px solid var(--paper-2)' : undefined,
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                }}>
                  近日の制作予定
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {shownUpcoming.map((item) => (
                    <div key={item.id} style={{
                      fontSize: 12, display: 'flex', gap: 6, alignItems: 'center',
                      padding: '3px 0', borderBottom: '1px solid var(--paper-2)',
                    }}>
                      <span style={{
                        fontFamily: 'var(--font-mono)', fontSize: 11,
                        color: 'var(--ink-3)', minWidth: 34, flexShrink: 0,
                      }}>
                        {mmdd(item.plan_date)}
                      </span>
                      <span style={{ fontWeight: 500, flex: 1, minWidth: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.work_title}{epText(item.episode_number)}
                        {item.stage_label && (
                          <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>
                            {' '}/ {item.stage_label}
                          </span>
                        )}
                      </span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, flexShrink: 0, fontSize: 11 }}>
                        {amtText(item.planned_amount, item.unit)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 表示件数が上限を超えている場合のみ、まとめて「全件表示」ボタンを出す */}
            {totalHidden > 0 && (
              <div style={{ marginTop: 10, textAlign: 'center' }}>
                <button
                  className="btn btn-outline btn-sm"
                  style={{ fontSize: 12 }}
                  onClick={() => setShowAllModal(true)}
                >
                  全件表示（+{totalHidden}件）
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>

    {/* ── 全件表示モーダル ── */}
    {showAllModal && (
      <div className="overlay" onClick={(e) => e.target === e.currentTarget && setShowAllModal(false)}>
        <div className="modal" style={{ maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
          <div className="modal-header">
            <div className="modal-title">制作予定をすべて見る</div>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowAllModal(false)}>✕</button>
          </div>
          <div className="modal-body" style={{ overflowY: 'auto' }}>
            {overdueItems.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--amber)', marginBottom: 6 }}>
                  ⚠ 過去の未完了予定（{overdueItems.length}件）
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {overdueItems.map((item) => (
                    <div key={item.id} style={{
                      fontSize: 13, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
                      padding: '4px 0', borderBottom: '1px solid var(--paper-2)',
                    }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-3)', minWidth: 32 }}>
                        {mmdd(item.plan_date)}
                      </span>
                      <span>{item.work_title}{epText(item.episode_number)}</span>
                      {item.stage_label && <span style={{ color: 'var(--ink-3)' }}>/ {item.stage_label}</span>}
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, marginLeft: 'auto' }}>
                        {amtText(item.planned_amount, item.unit)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {todayItems.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', marginBottom: 6,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                  今日の予定（{todayItems.length}件）
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {sortedToday.map((item) => (
                    <TodayItemRow
                      key={item.id}
                      item={item}
                      updatingId={updatingId}
                      onUpdate={handleUpdate}
                      onDefer={handleDefer}
                    />
                  ))}
                </div>
              </div>
            )}

            {upcomingItems.length > 0 && (
              <div>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', marginBottom: 6,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                  近日の予定（{upcomingItems.length}件）
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {upcomingItems.map((item) => (
                    <div key={item.id} style={{
                      fontSize: 12, display: 'flex', gap: 6, alignItems: 'center',
                      padding: '3px 0', borderBottom: '1px solid var(--paper-2)',
                    }}>
                      <span style={{
                        fontFamily: 'var(--font-mono)', fontSize: 11,
                        color: 'var(--ink-3)', minWidth: 34, flexShrink: 0,
                      }}>
                        {mmdd(item.plan_date)}
                      </span>
                      <span style={{ fontWeight: 500, flex: 1, minWidth: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.work_title}{epText(item.episode_number)}
                        {item.stage_label && (
                          <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>
                            {' '}/ {item.stage_label}
                          </span>
                        )}
                      </span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, flexShrink: 0, fontSize: 11 }}>
                        {amtText(item.planned_amount, item.unit)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  )
}
